import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { Helmet } from 'react-helmet';
import { useToasts } from 'react-toast-notifications';
import H1 from 'components/H1';
import H2 from 'components/H2';
import { Flex, Box, Button } from 'rebass';
import { Label, Input, Select } from '@rebass/forms';
import services from 'services/services';
import people_service from 'services/people';
import { FiMove, FiEdit, FiBookOpen, FiColumns, FiTrash2 } from 'react-icons/fi';
import helpers from 'utils/helpers';
import { PERSON_KEY_TYPES } from 'utils/constants';
import moment from 'moment-timezone';
import NiceModal from 'components/NiceModal';

import AppPageWrapper from 'components/AppPageWrapper';
import AppPageSubnav from 'components/AppPageWrapper/AppPageSubnav';
import AppPageBreadcrumbs from 'components/AppPageWrapper/AppPageBreadcrumbs';
import AppPageWrapperSectionHeader from 'components/AppPageWrapper/AppPageWrapperSectionHeader';
import AppPageWrapperSectionSubHeader from 'components/AppPageWrapper/AppPageWrapperSectionSubHeader';
import AppPageWrapperSectionBody from 'components/AppPageWrapper/AppPageWrapperSectionBody';
import ParticipantsPageSubNav from 'containers/ParticipantsPage/ParticipantsPageSubNav';
import ListWrapper from 'components/ListWrapper';
import ItemWrapper from 'components/ItemWrapper';
import InputSearch from 'components/InputSearch';
import { CreatePersonDataKey } from 'components/PersonDataKey';
import { List, arrayMove } from 'react-movable';
import { ActionButton } from 'components/Button/ActionButton';

function ParticipantsDataPage() {
    const [availableColumns, setAvailableColumns] = useState([]);
    const { addToast } = useToasts();
    const [editColumnLoading, setEditColumnLoading] = useState(false);
    const [selectedColumn, setSelectedColumn] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [showModalAddCustomColumn, setShowModalAddColumn] = useState(false);

    useEffect(() => {
        getAvailableColumnsXHR();
    }, []);

    function renderAvailableColumns() {
        let pkt_map = {};
        PERSON_KEY_TYPES.forEach(pkt => {
            pkt_map[pkt.type] = pkt.title;
        });

        let itemsList = [];
        availableColumns.forEach(ac => {
            if (!searchKeyword || helpers.findInString(searchKeyword, ac.title)) {
                itemsList.push(ac);
            }
        });

        let render = (
            <List
                values={itemsList}
                onChange={({ oldIndex, newIndex }) => {
                    console.log(oldIndex, newIndex);
                    itemsList = arrayMove(itemsList, oldIndex, newIndex);
                    setAvailableColumns(itemsList);

                    saveAvailableColumnsOrderXHR(itemsList);
                }}
                renderList={({ children, props }) => <div {...props}>{children}</div>}
                renderItem={({ value, index, props, isDragged, isSelected }) => {
                    let ac = value;

                    let reorderElement = (
                        <Button
                            data-movable-handle
                            type="button"
                            variant="secondary-gray"
                            className="secondary-icon"
                            tabIndex={-1}
                            mr={2}
                            style={{
                                cursor: isDragged ? 'grabbing' : 'grab'
                            }}
                        >
                            <FiMove />
                        </Button>
                    );

                    return (
                        <div {...props}>
                            <ItemWrapper style={{ color: 'black' }} key={ac.id}>
                                <Box width={1 / 4}>{ac.title}</Box>
                                <Box width={1 / 8}>{pkt_map[ac.type]}</Box>
                                <Box width={1 / 8} style={{ maxHeight: '200px', overflow: 'scroll', userSelect: 'text' }}>
                                    {(ac.type == 'dropdown' || ac.type == 'checkbox') &&
                                        ac.definition.map(d => {
                                            if (!d) {
                                                return;
                                            }
                                            return <div>{d.label}</div>;
                                        })}
                                </Box>
                                <Box width={1 / 4}>
                                    {reorderElement}
                                    {ac.can_delete == true && (
                                        <>
                                            <Button
                                                type="button"
                                                mr={2}
                                                variant="secondary-gray"
                                                onClick={e => {
                                                    setSelectedColumn(ac);
                                                    setShowModalAddColumn(true);
                                                }}
                                            >
                                                <FiEdit /> Edit
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="secondary-gray"
                                                className="secondary-icon"
                                                onClick={() => {
                                                    if (
                                                        // eslint-disable-next-line
                                                        confirm(
                                                            `Are you sure you want to delete the "${ac.title}" column and all of the associated panelist data?`
                                                        )
                                                    ) {
                                                        deleteColumnXHR(ac);
                                                    }
                                                }}
                                            >
                                                <FiTrash2 />
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </ItemWrapper>
                        </div>
                    );
                }}
            />
        );

        return render;
    }

    function deleteColumnXHR(column) {
        services
            .deleteCustomDataColumn(column.id)
            .then(() => {
                addToast('Successfully deleted', {
                    appearance: 'success',
                    autoDismiss: true
                });
                getAvailableColumnsXHR(true);
            })
            .catch(error => {
                const errorText = services.parseXhrErrors(error);

                addToast(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    function getAvailableColumnsXHR(bust_cache) {
        services
            .getCustomDataColumnsXHR(bust_cache)
            .then(columns => {
                // sort by the "order"
                columns.sort((a, b) => {
                    if (a.order < b.order) return -1;
                    if (a.order > b.order) return 1;
                    return 0;
                });
                setAvailableColumns(columns);
            })
            .catch(error => {
                const errorText = services.parseXhrErrors(error);

                addToast(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    function saveAvailableColumnsOrderXHR(itemsList) {
        itemsList.forEach((item, index) => {
            item.order = index;
        });

        services
            .saveCustomDataColumnsOrder(itemsList)
            .then(columns => {})
            .catch(error => {
                const errorText = services.parseXhrErrors(error);

                addToast(errorText, {
                    appearance: 'error',
                    autoDismiss: true
                });
            });
    }

    function onChangeTitle(key, val) {
        setSelectedColumn(prev => ({ ...prev, [key]: val }));
    }

    function onSearchKeywordChange(e) {
        setSearchKeyword(e.target.value);
    }

    const num_matched = availableColumns.length;

    return (
        <Flex style={{ width: '100%' }}>
            <AppPageWrapper>
                <Helmet>
                    <title>Panel - Panelist Properties</title>
                </Helmet>

                <AppPageSubnav>
                    <ParticipantsPageSubNav />
                </AppPageSubnav>

                <AppPageWrapperSectionHeader className="experience">
                    <Box>
                        <H1>Panel Properties</H1>
                        <Box className="experience-description">
                            Create as many properties as your panel needs.<br/>
                            <a href="#" data-beacon-article="63032877abed727489465a04">
                                <FiBookOpen /> How to get started
                            </a>
                        </Box>
                    </Box>
                    <Box mt={2}>
                        <ActionButton
                            title="New Property"
                            action={() => {
                                setSelectedColumn(false);
                                setShowModalAddColumn(true);
                            }}
                            keyLabel="N"
                            mr={0}
                        />
                    </Box>
                </AppPageWrapperSectionHeader>
                <AppPageWrapperSectionBody>
                    <Box mx={4} my={4} width="100%">
                        <Flex style={{ width: '100%' }} justifyContent="space-between">
                            <Box style={{ color: 'black', marginTop: '4px' }} mb={4}>
                                <strong>
                                    {num_matched} {num_matched == 1 ? 'property' : 'properties'} matched
                                </strong>
                            </Box>
                            <Box>
                                <InputSearch value={searchKeyword} onChange={onSearchKeywordChange} />
                            </Box>
                        </Flex>
                        <ListWrapper
                            style={{
                                position: 'relative',
                                border: '1px solid #eee',
                                borderRadius: '4px'
                            }}
                        >
                            <ItemWrapper className="header-row">
                                <Box width={1 / 4}>Title</Box>
                                <Box width={1 / 8}>Type</Box>
                                <Box width={1 / 8}>Definition</Box>
                                <Box width={1 / 4}>Actions</Box>
                            </ItemWrapper>
                            {renderAvailableColumns()}
                            <ItemWrapper style={{ border: 0 }}>
                                {num_matched} result{num_matched == 1 ? '' : 's'}
                            </ItemWrapper>
                        </ListWrapper>
                        <br />
                        <br />
                    </Box>

                    <NiceModal
                        isOpen={showModalAddCustomColumn}
                        shouldCloseOnOverlayClick
                        onRequestClose={() => {
                            setShowModalAddColumn(false);
                            setSelectedColumn(false);
                            getAvailableColumnsXHR(true);
                        }}
                        title={`${selectedColumn ? 'Edit' : 'Create a new'} property`}
                    >
                        <CreatePersonDataKey
                            edit={selectedColumn}
                            onSuccess={() => {
                                getAvailableColumnsXHR(true);
                                setShowModalAddColumn(false);
                            }}
                            onClose={() => {
                                setShowModalAddColumn(false);
                                setSelectedColumn(false);
                                getAvailableColumnsXHR(true);
                            }}
                        />
                    </NiceModal>
                </AppPageWrapperSectionBody>
            </AppPageWrapper>
        </Flex>
    );
}

export default ParticipantsDataPage;
